import React, { useState } from 'react'

interface ContactModalProps {
  isOpen: boolean
  onClose: () => void
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    businessType: '',
    message: ''
  });
  

  const [isSending, setIsSending] = useState(false)
  const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSending(true)
    setStatus(null)

    try {
      // Using SMTP.js library (include in your HTML)
      // Add this to your index.html: <script src="https://smtpjs.com/v3/smtp.js"></script>
      
      // @ts-ignore - SMTPJS is loaded globally
      await window.Email.send({
        Host: import.meta.env.VITE_SMTP_HOST,
        Username: import.meta.env.VITE_SMTP_USERNAME,
        Password: import.meta.env.VITE_SMTP_PASSWORD,
        To: import.meta.env.VITE_SMTP_TO,
        From: formData.email,
        Subject: `New Contact from ${formData.name}`,
        Body: `
          <h3>New Contact Form Submission</h3>
          <p><strong>Name:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Company:</strong> ${formData.company || 'N/A'}</p>
          <p><strong>Phone:</strong> ${formData.phone || 'N/A'}</p>
          <p><strong>Business Type:</strong> ${formData.businessType || 'N/A'}</p>
          <p><strong>Message:</strong> ${formData.message}</p>
        `,
        Port: 587,
        SecureSocket: false // STARTTLS
      })

      setStatus({ type: 'success', message: 'Message sent successfully!' })
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        businessType: '',
        message: ''
      })
      setTimeout(onClose, 2000)
    } catch (error) {
      console.error('Error sending email:', error)
      setStatus({ type: 'error', message: 'Failed to send message. Please try again.' })
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`absolute top-0 right-0 h-full w-full max-w-lg transform transition-transform duration-500 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full glass-card-intense border-l border-brand-blue/30 overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold gradient-text">Get Started Today</h2>
                <p className="text-gray-300 text-sm">Let's discuss your payment needs</p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full glass-card flex items-center justify-center hover:bg-red-500/20 transition-colors"
                disabled={isSending}
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Status Message */}
            {status && (
              <div className={`mb-4 p-3 rounded-lg ${
                status.type === 'success' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {status.message}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 glass-card rounded-lg border border-brand-blue/20 bg-transparent text-white placeholder-gray-400 focus:border-brand-blue focus:outline-none transition-colors"
                  placeholder="John Doe"
                  disabled={isSending}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 glass-card rounded-lg border border-brand-blue/20 bg-transparent text-white placeholder-gray-400 focus:border-brand-blue focus:outline-none transition-colors"
                  placeholder="john@company.com"
                  disabled={isSending}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 glass-card rounded-lg border border-brand-blue/20 bg-transparent text-white placeholder-gray-400 focus:border-brand-blue focus:outline-none transition-colors"
                  placeholder="Your Company"
                  disabled={isSending}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 glass-card rounded-lg border border-brand-blue/20 bg-transparent text-white placeholder-gray-400 focus:border-brand-blue focus:outline-none transition-colors"
                  placeholder="+1 (555) 123-4567"
                  disabled={isSending}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Business Type
                </label>
                <select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 glass-card rounded-lg border border-brand-blue/20 bg-brand-dark text-white focus:border-brand-blue focus:outline-none transition-colors"
                  disabled={isSending}
                >
                  <option value="">Select your industry</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="gaming">Online Gaming</option>
                  <option value="forex">Forex</option>
                  <option value="crypto">Cryptocurrency</option>
                  <option value="nutra">Nutra</option>
                  <option value="saas">SaaS</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 glass-card rounded-lg border border-brand-blue/20 bg-transparent text-white placeholder-gray-400 focus:border-brand-blue focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your payment processing needs..."
                  disabled={isSending}
                />
              </div>

              <button
                type="submit"
                className="w-full cta-button neon-glow px-6 py-3 rounded-lg font-semibold text-white hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>

            {/* Contact Info */}
            <div className="mt-8 pt-6 border-t border-brand-blue/20">
              <h3 className="text-lg font-semibold mb-4 text-white">Other Ways to Reach Us</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-blue to-brand-purple rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-gray-300">support@universalpays.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-blue to-brand-purple rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <span className="text-gray-300">+1 365 829 0725</span>
                  <span className="text-gray-300">+44 7397 022602</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-blue to-brand-purple rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-300">24/7 Support Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactModal